import { Box, SxProps } from "@outschool/backpack";
import { onlineClassesPath } from "@outschool/routes";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  Container,
  LegacyPopover,
} from "@outschool/ui-legacy-component-library";
import {
  useClickOutsideRefEventListener,
  useEventListener,
  useLinkComponent,
  useNavigation,
  useOnScreen,
  useWindowSize,
} from "@outschool/ui-utils";
import lodashDebounce from "lodash/debounce";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { CategoryPartial } from "./navbarCategories";

export default function DesktopCategoryNavbar({
  categories,
  sx = {},
  onClickLink,
}: {
  categories: CategoryPartial[];
  sx?: SxProps;
  onClickLink?: () => void;
}) {
  const navbarRef = useRef<HTMLDivElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const windowSize = useWindowSize();
  const document = (
    typeof window !== "undefined" ? window.document : null
  ) as Document | null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const select = useCallback(lodashDebounce(setSelectedCategory, 250), [
    setSelectedCategory,
  ]);
  const deselect = useCallback(() => select(undefined), [select]);

  useClickOutsideRefEventListener(document, navbarRef, deselect);

  const onEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        deselect();
      }
    },
    [deselect]
  );
  useEventListener(document, "keyup", onEscape);

  const mouseListener = useCallback(
    (event: MouseEvent) => {
      const isOutside = [navbarRef].every(
        ref => !ref.current?.contains(event.target as HTMLElement)
      );
      if (isOutside) {
        deselect();
      }
    },
    [navbarRef, deselect]
  );
  useEventListener(document, "mouseover", mouseListener);

  const renderSubcategoryCallback = React.useCallback(
    (category: CategoryPartial) => {
      return (isActive: boolean) => (
        <SubcategoryPopupContent
          isActive={isActive}
          category={category}
          closePopup={deselect}
        />
      );
    },
    [deselect]
  );

  const categoriesNavbarItemMemo = React.useMemo(() => {
    return categories.map((category: CategoryPartial, index: number) => (
      <NavbarItem
        key={`${category.slug}:${
          category?.subcategories?.length || 0
        }:${index}`}
        parentRef={navbarRef}
        uniqueId={category.slug}
        label={category.nameTitleCased}
        renderPopupContents={renderSubcategoryCallback(category)}
        isSelected={selectedCategory === category.slug}
        select={() => select(category.slug)}
        link={
          category.can_link
            ? category.path
              ? category.path
              : onlineClassesPath(category.slug, category.basePath)
            : undefined
        }
        onClickLink={onClickLink ? onClickLink : undefined}
      />
    ));
  }, [
    categories,
    renderSubcategoryCallback,
    select,
    selectedCategory,
    onClickLink,
  ]);
  return (
    <Box sx={sx}>
      <Container ref={navbarRef}>
        <FlexWithCutoff windowSize={windowSize}>
          {categoriesNavbarItemMemo}
        </FlexWithCutoff>
      </Container>
    </Box>
  );
}

function SubcategoryPopupContent({
  category,
  isActive,
  closePopup,
  onClickLink,
}: {
  category: CategoryPartial;
  isActive: boolean;
  closePopup: () => void;
  onClickLink?: () => void;
}) {
  const subcategoriesMemo = React.useMemo(() => {
    return (
      <>
        {category.subcategories?.map((subcategory, index) => (
          <React.Fragment key={`${category.slug}:${subcategory.slug}:${index}`}>
            <CategoryLink
              onMouseUp={closePopup}
              isActive={isActive}
              category={subcategory}
              uniqueId={`${category.slug}/${subcategory.slug}`}
              sx={{
                fontWeight: "500",
                fontSize: "1.1em",
                paddingTop: "8px",
                paddingBottom: subcategory.subcategories?.length ? 0 : "18px",
              }}
              onClickLink={onClickLink ? onClickLink : undefined}
            >
              {subcategory.nameTitleCased}
            </CategoryLink>
            {subcategory.subcategories?.map((nestedSubcategory, i, a) => (
              <CategoryLink
                key={`${category.slug}:${nestedSubcategory.slug}`}
                onMouseUp={closePopup}
                isActive={isActive}
                category={nestedSubcategory}
                uniqueId={`${category.slug}/${nestedSubcategory.slug}`}
                sx={{
                  paddingTop: "8px",
                  paddingBottom: i === a.length - 1 ? "18px" : "8px",
                }}
                onClickLink={onClickLink ? onClickLink : undefined}
              >
                {nestedSubcategory.nameTitleCased}
              </CategoryLink>
            ))}
          </React.Fragment>
        ))}
      </>
    );
  }, [category, closePopup, isActive, onClickLink]);

  if (!category.subcategories || category.subcategories?.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        margin: "10px",
      }}
    >
      <FlexWithWrappingColumns maxHeight="400px">
        {subcategoriesMemo}
      </FlexWithWrappingColumns>
    </Box>
  );
}

function FlexWithWrappingColumns({
  children,
  maxHeight,
}: {
  children: React.ReactNode;
  maxHeight: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const [width, setWidth] = useState<Number>();
  const onScreen = useOnScreen(ref?.current ?? undefined);

  useEffect(() => {
    if (ref.current) {
      // @ts-ignore
      const childNodes = [...ref.current.childNodes];

      const newWidth = Math.max(
        // @ts-ignore
        ...childNodes.map(node => node.offsetLeft + node.clientWidth)
      );
      setWidth(newWidth);
    }
  }, [ref, setWidth, onScreen]);

  return (
    <Box
      flex
      ref={ref}
      sx={{
        flexDirection: "column",
        flexWrap: "wrap",
        maxHeight,
        width: width ? `${width}px` : undefined,
      }}
    >
      {children}
    </Box>
  );
}

function CategoryLink({
  category,
  children,
  uniqueId,
  isActive,
  onMouseUp,
  sx,
  onClickLink,
}: {
  category: CategoryPartial;
  children: React.ReactNode;
  uniqueId: string;
  isActive: boolean;
  onMouseUp: () => void;
  sx?: SxProps;
  onClickLink?: () => void;
}) {
  const Link = useLinkComponent();
  return (
    <Box
      sx={[
        {
          lineHeight: 1,
          paddingX: "6px",
          width: "208px",

          "& > a": {
            color: "grey.900",
            fontSize: "0.875em",

            "&:hover, &:focus-within, &:focus, &:focus-visible": {
              color: "primary.700",
              outlineOffset: "4px",
              textDecoration: "underline",
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* Rendering without link when not active prevents impression tracking, but still allows popup sizing */}
      {isActive ? (
        <Link
          to={category.path ? category.path : onlineClassesPath(category.slug)}
          onMouseUp={onMouseUp}
          trackingName="category-navbar-desktop-popup-item"
          trackingUniqueId={uniqueId}
          trackViews={true}
          onClick={onClickLink}
        >
          {children}
        </Link>
      ) : (
        children
      )}
    </Box>
  );
}

function NavbarItem({
  uniqueId,
  label,
  renderPopupContents,
  parentRef,
  select,
  isSelected,
  link,
  onClickLink,
}: {
  uniqueId: string;
  label: string | React.ReactElement;
  renderPopupContents: (isActive: boolean) => React.ReactNode;
  parentRef: React.RefObject<HTMLElement>;
  select: () => void;
  isSelected: boolean;
  link?: string;
  onClickLink?: () => void;
}) {
  const popoverRef = useRef<HTMLElement>();
  const buttonRef = useRef<HTMLElement>();

  const navigate = useNavigation();
  const goToLink = useCallback(() => {
    if (link) {
      navigate(link);
    }
  }, [link, navigate]);

  const handleClick = () => {
    if (onClickLink) {
      onClickLink();
    }
    select(); // Call the second function
  };

  const contentMemo = React.useMemo(() => {
    return (
      <Box
        sx={{
          backgroundColor: "common.white",
          visibility: isSelected ? "visible" : "hidden",
        }}
      >
        {renderPopupContents(isSelected)}
      </Box>
    );
  }, [isSelected, renderPopupContents]);

  return (
    <LegacyPopover
      ref={popoverRef as React.Ref<HTMLElement>}
      placement="hide"
      slideIn={false}
      isHidden={!isSelected}
      animationDuration="0.2s"
      mainPlacement="bottom-start"
      modifiers={{
        preventOverflow: {
          enabled: true,
          boundariesElement: parentRef.current ?? undefined,
          priority: ["left", "right"],
        },
      }}
      renderButton={(ref: React.Ref<HTMLElement>) => (
        <NavbarButton
          isActive={isSelected}
          ref={ref}
          onClick={handleClick}
          onMouseUp={goToLink}
          onMouseEnter={select}
          buttonRef={buttonRef}
          label={label}
          uniqueId={uniqueId}
        />
      )}
    >
      {contentMemo}
    </LegacyPopover>
  );
}

enum ValidationStates {
  valid,
  invalid,
  calculating,
}

function FlexWithCutoff({
  children,
  windowSize,
}: {
  children: React.ReactNode;
  windowSize: number[];
}) {
  const containerRef = useRef<HTMLElement>(null);

  const childArray = Array.isArray(children) ? children : [children];
  const [numVisible, setNumVisible] = useState(Math.min(9, childArray.length));

  const [validationState, setValidationState] = useState(
    ValidationStates.valid
  );

  const debounced = lodashDebounce(
    () => setValidationState(ValidationStates.invalid),
    250,
    {
      leading: true,
      trailing: true,
    }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const invalidateLayout = useCallback(debounced, [setValidationState]);

  useEffect(() => {
    return () => {
      if (!!debounced) {
        debounced.cancel();
        setValidationState(ValidationStates.invalid);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    invalidateLayout();
  }, [invalidateLayout, windowSize, childArray.length]);

  useEffect(() => {
    if (validationState === ValidationStates.invalid) {
      setNumVisible(childArray.length);
      setValidationState(ValidationStates.calculating);
    }
  }, [childArray.length, validationState, setValidationState]);

  useEffect(() => {
    if (validationState === ValidationStates.calculating) {
      const containerElement = containerRef.current;
      if (containerElement) {
        const overflowIndex = (
          Array.from(containerElement.childNodes) as HTMLElement[]
        ).findIndex(
          node =>
            node.offsetWidth + node.offsetLeft > containerElement.offsetWidth
        );
        if (overflowIndex > 0) {
          setNumVisible(overflowIndex);
        }
      }
      setValidationState(ValidationStates.valid);
    }
  }, [containerRef, validationState, setValidationState]);

  return (
    <Box
      flex
      ref={containerRef}
      sx={{
        position: "relative",
        height: "48px",
        columnGap: "20px",
        justifyContent: "space-between",
        alignItems: "stretch",
      }}
    >
      {childArray.slice(0, numVisible).map((child, i) => (
        <Box key={`child_${i}`}>{child}</Box>
      ))}
    </Box>
  );
}

type NavbarButtonProps = {
  buttonRef: any;
  label: string | React.ReactNode;
  uniqueId: string;
  onClick: () => void;
  onMouseEnter: (event: MouseEvent) => void;
  onMouseUp: (event: MouseEvent) => void;
  isActive: boolean;
};

const NavbarButton = React.forwardRef<HTMLElement, NavbarButtonProps>(
  (
    { buttonRef, label, uniqueId, onClick, onMouseEnter, onMouseUp, isActive },
    ref
  ) => {
    const ButtonMemo = React.useMemo(() => {
      return (
        <TrackedButton
          variant="link"
          ref={buttonRef}
          trackingName="category-navbar-desktop-item"
          trackingUniqueId={uniqueId}
          trackViews={true}
          onClick={onClick}
          onMouseUp={onMouseUp}
          sx={{
            whitSpace: "nowrap",
            height: "100%",
          }}
        >
          {label}
        </TrackedButton>
      );
    }, [buttonRef, label, onClick, onMouseUp, uniqueId]);

    return (
      <Box
        ref={ref}
        onMouseEnter={onMouseEnter}
        sx={{
          height: "48px",
          lineHeight: "48px",
          paddingTop: "2px",
          borderBottom: "2px solid rgba(0,0,0,0)",

          "& > button": {
            color: isActive ? "primary.700" : "grey.900",
            fontWeight: "fontWeightRegular",
          },

          borderColor: isActive ? "primary.700" : undefined,

          "&:hover": {
            borderColor: "primary.700",

            "& > button:not(:disabled)": {
              color: "primary.700",
              textDecoration: "none",
            },
          },

          "&:focus-within": {
            "& > button:not(:disabled)": {
              color: "primary.700",
              textDecoration: "none",
            },
          },
        }}
      >
        {ButtonMemo}
      </Box>
    );
  }
);
